import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";

export type FooterQuote = {
  id: number;
  text: string;
  remaining: number;
  cycle: number;
};

import FALLBACK_QUOTES from "../constants/footerQuotes.json";

export default function FooterEasterEgg() {
  const getRandomFallbackQuote = useCallback((advance: boolean) => {
    let seenArray: number[] = [];
    try {
      const stored = sessionStorage.getItem("caio_footer_seen_quotes");
      if (stored) {
        seenArray = JSON.parse(stored);
      }
    } catch (_) {}

    const seenSet = new Set<number>(seenArray);
    let unseen = FALLBACK_QUOTES.map((_, i) => i).filter((i) => !seenSet.has(i));
    let cycle = 1;

    if (unseen.length === 0) {
      seenSet.clear();
      unseen = FALLBACK_QUOTES.map((_, i) => i);
      cycle = 2;
    }

    const randomIdx = unseen[Math.floor(Math.random() * unseen.length)];
    if (advance || seenArray.length === 0) {
      seenSet.add(randomIdx);
      try {
        sessionStorage.setItem("caio_footer_seen_quotes", JSON.stringify(Array.from(seenSet)));
      } catch (_) {}
    }

    return {
      id: randomIdx + 1,
      text: FALLBACK_QUOTES[randomIdx],
      remaining: unseen.length - 1,
      cycle,
    };
  }, []);

  const [quote, setQuote] = useState<FooterQuote | null>(() => getRandomFallbackQuote(true));
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const fetchQuote = useCallback(async (advance: boolean) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(
        advance ? "/api/footer-quote/next" : "/api/footer-quote",
        {
          method: advance ? "POST" : "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Footer quote request failed: ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Non-JSON content type received from API route");
      }

      const result = (await response.json()) as FooterQuote;
      if (result && result.text) {
        setQuote(result);
      }
    } catch (error) {
      const fallbackQuote = getRandomFallbackQuote(advance);
      setQuote(fallbackQuote);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [getRandomFallbackQuote]);

  useEffect(() => {
    void fetchQuote(true);
  }, [fetchQuote]);

  if (!quote) return null;

  return (
    <div className="w-full pt-6 mt-6 border-t border-brand-espresso/40 flex justify-center">
      <button
        type="button"
        onClick={() => void fetchQuote(true)}
        disabled={loading}
        aria-live="polite"
        title="Click to rotate observation"
        className="group max-w-3xl flex items-center justify-center gap-2 text-center text-xs sm:text-sm font-mono text-brand-sand/40 hover:text-brand-orange/90 transition-colors duration-300 cursor-pointer select-none focus:outline-none"
      >
        <span className="opacity-40 group-hover:opacity-100 transition-opacity shrink-0">
          {loading ? (
            <RefreshCw className="w-3 h-3 animate-spin text-brand-orange" />
          ) : (
            <Sparkles className="w-3 h-3 text-brand-orange" />
          )}
        </span>
        <span className="leading-relaxed font-light">
          {loading ? "Consulting governance committee…" : quote.text}
        </span>
      </button>
    </div>
  );
}
